import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedRole = localStorage.getItem('role');
      
      if (storedToken) {
        try {
          if (storedRole === 'admin') {
            // For admin, we already have the data in localStorage
            const adminData = JSON.parse(localStorage.getItem('adminData') || '{}');
            setAdmin(adminData);
          } else {
            const response = await axios.get(`${API_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            setUser(response.data);
          }
          setToken(storedToken);
        } catch (error) {
          console.error('Auth init error:', error);
          logout();
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('token', newToken);
    localStorage.setItem('role', 'user');
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const adminLogin = async (username, password) => {
    const response = await axios.post(`${API_URL}/auth/admin/login`, { username, password });
    const { token: newToken, admin: adminData } = response.data;
    
    localStorage.setItem('token', newToken);
    localStorage.setItem('role', 'admin');
    localStorage.setItem('adminData', JSON.stringify(adminData));
    setToken(newToken);
    setAdmin(adminData);
    
    return adminData;
  };

  const register = async (name, email, phone, password) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      name,
      email,
      phone,
      password
    });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('adminData');
    setToken(null);
    setUser(null);
    setAdmin(null);
  };

  const refreshUser = async () => {
    if (token && !admin) {
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Refresh user error:', error);
      }
    }
  };

  // Method to set auth data directly (used for auto-login after verification)
  const setAuthData = (newToken, userData, role = 'user') => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('role', role);
    setToken(newToken);
    if (role === 'admin') {
      localStorage.setItem('adminData', JSON.stringify(userData));
      setAdmin(userData);
    } else {
      setUser(userData);
    }
  };

  const value = {
    user,
    admin,
    token,
    loading,
    isAuthenticated: !!token && (!!user || !!admin),
    isAdmin: !!admin,
    login,
    adminLogin,
    register,
    logout,
    refreshUser,
    setAuthData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
